import {
    getConfig,
    GlobalConfigProperties,
} from '../../api/ABHub/ABHubGlobalConfig';
import { hasAccountInfo } from '../../../HubLayout/util/authUtils';
import { CapabilityReducerState } from './capabilityReducer';
import {
    getSpecificClaimValue,
    getTrueFalseClaimValue,
    hasAnyRoleClaim,
} from './capabilityUtils';

export enum CapabilityState {
    unavailable = -1,
    unknown = 0,
    available = 1,
}

export enum Capability {
    // env/cloud
    dev = 'Development',
    test = 'Test',
    ppe = 'PPE',
    prod = 'Production',
    public = 'Public',
    ussec = 'USSec',
    usnat = 'USNat',
    sovmock = 'Sovmock',

    // user claims
    ex_tent = 'ex_tent',
    rx_tent = 'rx_tent',
    // DEPRECATED legacy roles — kept for backwards compatibility during rollout of the
    // consolidated Srm.Admin / Srm.Contributor / Srm.Reader hierarchy. Prefer srmContributorRole
    // or srmAdminRole in new code. See LEGACY_ROLE_FALLBACK below.
    artifactRereplicateRole = 'artifactRereplicateRole',
    artifactActivateRole = 'artifactActivateRole',
    betaUIFeaturesRole = 'betaUIFeaturesRole',
    bridgeAdminRole = 'bridgeAdminRole',
    // Consolidated role hierarchy: Admin > Contributor > Reader.
    srmAdminRole = 'srmAdminRole',
    srmContributorRole = 'srmContributorRole',
    srmReaderRole = 'srmReaderRole',
}

// =============================================================================
// LEGACY_ROLE_FALLBACK
// -----------------------------------------------------------------------------
// The following constants keep users with the deprecated roles
// (Artifact.Rereplicate, Artifact.Activate, Beta.UI, Bridge.Admin) working while
// the consolidated Srm.* role hierarchy is rolled out. To FULLY DEPRECATE the
// legacy roles, delete the LEGACY_* arrays below and remove the lines marked
// "LEGACY_ROLE_FALLBACK" inside getCapabilityState. No call-site changes needed.
// =============================================================================
const LEGACY_ADMIN_FALLBACK_ROLES = ['Bridge.Admin'];
const LEGACY_CONTRIBUTOR_FALLBACK_ROLES = [
    'Artifact.Rereplicate',
    'Artifact.Activate',
    'Beta.UI',
];

// Admin claim is sufficient for any admin-, contributor-, or reader-gated feature.
const ADMIN_ROLES = ['Srm.Admin'];
// Contributor claim is sufficient for any contributor- or reader-gated feature.
// Admin implies Contributor.
const CONTRIBUTOR_ROLES = ['Srm.Contributor', ...ADMIN_ROLES];

const configMatchesCapability = (
    property: GlobalConfigProperties,
    capability: Capability
): boolean => getConfig(property).toLowerCase() === capability.toLowerCase();

// Define how to get capability state for a given capability.
// To track info (eg: logged-in user) as state, add it to capabilityReducer.ts.
export const getCapabilityState = (
    capability: Capability,
    state: CapabilityReducerState
): CapabilityState => {
    switch (capability) {
        case Capability.dev:
        case Capability.test:
        case Capability.ppe:
        case Capability.prod:
            return configMatchesCapability(
                GlobalConfigProperties.Environment,
                capability
            )
                ? CapabilityState.available
                : CapabilityState.unavailable;
        case Capability.public:
        case Capability.ussec:
        case Capability.usnat:
        case Capability.sovmock:
            return configMatchesCapability(
                GlobalConfigProperties.Cloud,
                capability
            )
                ? CapabilityState.available
                : CapabilityState.unavailable;
        case Capability.ex_tent:
            return getTrueFalseClaimValue(state.agpUser, 'ex_tent');
        case Capability.rx_tent:
            return getTrueFalseClaimValue(state.agpUser, 'rx_tent');

        // ---- Contributor-tier legacy capabilities ----
        // Satisfied by the original claim (LEGACY_ROLE_FALLBACK) OR by any role
        // higher in the new hierarchy (Srm.Contributor, Srm.Admin).
        case Capability.artifactRereplicateRole:
            return hasAnyRoleClaim(state.agpUser, [
                'Artifact.Rereplicate', // LEGACY_ROLE_FALLBACK
                ...CONTRIBUTOR_ROLES,
            ]);
        case Capability.artifactActivateRole:
            return hasAnyRoleClaim(state.agpUser, [
                'Artifact.Activate', // LEGACY_ROLE_FALLBACK
                ...CONTRIBUTOR_ROLES,
            ]);
        case Capability.betaUIFeaturesRole:
            return hasAnyRoleClaim(state.agpUser, [
                'Beta.UI', // LEGACY_ROLE_FALLBACK
                ...CONTRIBUTOR_ROLES,
            ]);

        // ---- Admin-tier legacy capability ----
        case Capability.bridgeAdminRole:
            return hasAnyRoleClaim(state.agpUser, [
                ...LEGACY_ADMIN_FALLBACK_ROLES, // LEGACY_ROLE_FALLBACK
                ...ADMIN_ROLES,
            ]);

        // ---- Consolidated hierarchy ----
        case Capability.srmAdminRole:
            if (state.nonAdminMode) return CapabilityState.unavailable;
            return hasAnyRoleClaim(state.agpUser, ADMIN_ROLES);
        case Capability.srmContributorRole:
            // Admin implies Contributor. Legacy contributor-tier claims also
            // satisfy this check during the deprecation window.
            return hasAnyRoleClaim(state.agpUser, [
                ...CONTRIBUTOR_ROLES,
                ...LEGACY_CONTRIBUTOR_FALLBACK_ROLES, // LEGACY_ROLE_FALLBACK
            ]);
        case Capability.srmReaderRole: {
            // SRM Reader is the default role for any authenticated (tented) user.
            // Explicit Srm.Reader claim or any higher-tier role also satisfies it.
            const byRole = hasAnyRoleClaim(state.agpUser, [
                'Srm.Reader',
                ...CONTRIBUTOR_ROLES,
            ]);
            if (byRole === CapabilityState.available) {
                return CapabilityState.available;
            }
            // Default: any authenticated user is a reader.
            return hasAccountInfo(state.agpUser)
                ? CapabilityState.available
                : getSpecificClaimValue(state.agpUser, 'roles', 'Srm.Reader');
        }
        default:
            return CapabilityState.unknown;
    }
};
